import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import Flex, { AlignItems, FlexDirection } from "@galaxy-io/dls/layout/Flex";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { AuthLayout } from "@/features/authentication/auth-layout";

/**
 * The development identity provider.
 *
 * Exists so a new engineer can sign in with no external service. The API
 * refuses `AUTH_MODE=dev` in production, so there is no configuration in which
 * this reaches a real deployment.
 */
export function DevSignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState("admin@acme.example");
  const [error, setError] = useState<string | null>(null);

  const signIn = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/auth/dev/sign-in", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? "Could not sign in.");
      }
    },
    onSuccess: onSignedIn,
    onError: (cause) => setError(cause instanceof Error ? cause.message : "Could not sign in."),
  });

  return (
    <AuthLayout>
      <Flex alignItems={AlignItems.START} direction={FlexDirection.COLUMN} gap={12}>
        <TextInput
          label="Email"
          value={email}
          placeholder="you@company.com"
          onChange={(value: string) => setEmail(value)}
          error={error ?? undefined}
        />
        <Button
          label="Continue"
          variant={ButtonVariant.PRIMARY}
          isLoading={signIn.isPending}
          fillWidth
          onClick={() => signIn.mutate()}
        />
        <Text size={TextSize.CAPTION} variant={TextVariant.TERTIARY}>
          Development sign-in. Passwords are never handled by this application.
        </Text>
      </Flex>
    </AuthLayout>
  );
}
